// SCME v0.2 · Fase 2 · Ecosistema de Plugins (UI solo lectura)
// ---------------------------------------------------------------------------
// Consulta SÓLO LECTURA de los artefactos generados en Fase 0:
//   • PLUGIN_REGISTRY.json  → tabla + detalle
//   • DEPENDENCY_MAP.json   → grafo de dependencias por plugin
//   • REUSE_MATRIX.json     → estado de reutilización por proyecto
//
// Reglas obligatorias respetadas:
//   • No instala, no exporta, no importa, no sincroniza.
//   • Los proyectos permanecen 100% independientes.
//   • Ningún dato se escribe en Mongo.
// ---------------------------------------------------------------------------
import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Puzzle,
  Search,
  Loader2,
  ShieldCheck,
  Info,
  X,
  Layers3,
  GitBranch,
  Server,
  Database,
  KeyRound,
  BookOpen,
  Download,
  Upload,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import PluginsExportDialog from "./PluginsExportDialog";
import PluginsImportDialog from "./PluginsImportDialog";

const STATUS_STYLES = {
  reusable:   { label: "Reutilizable", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  candidate:  { label: "Candidato",    cls: "bg-amber-100 text-amber-800 border-amber-200" },
  draft:      { label: "Borrador",     cls: "bg-neutral-100 text-neutral-700 border-neutral-200" },
  deprecated: { label: "Obsoleto",     cls: "bg-rose-100 text-rose-800 border-rose-200" },
};

const REUSE_BAND = (score) => {
  if (score >= 90) return "text-emerald-700";
  if (score >= 70) return "text-lime-700";
  if (score >= 50) return "text-amber-700";
  return "text-rose-700";
};

const COUPLING_BAND = (score) => {
  // Nivel de acoplamiento = inverso al score de reutilización
  if (score >= 90) return { label: "Muy bajo", cls: "bg-emerald-50 text-emerald-800" };
  if (score >= 70) return { label: "Bajo",     cls: "bg-lime-50 text-lime-800" };
  if (score >= 50) return { label: "Medio",    cls: "bg-amber-50 text-amber-800" };
  return { label: "Alto", cls: "bg-rose-50 text-rose-800" };
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.candidate;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${s.cls}`}>
      {s.label}
    </span>
  );
};

const ReusePill = ({ score }) => (
  <div className="flex items-center gap-2 min-w-[92px]">
    <div className="flex-1 h-1.5 rounded-full bg-neutral-200 overflow-hidden">
      <div
        className="h-full bg-gradient-to-r from-emerald-400 to-lime-500"
        style={{ width: `${Math.max(0, Math.min(100, score || 0))}%` }}
      />
    </div>
    <span className={`text-[11px] font-mono tabular-nums ${REUSE_BAND(score || 0)}`}>
      {score || 0}%
    </span>
  </div>
);

function PluginDetailDialog({ pluginId, open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open || !pluginId) return;
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const resp = await api.get(`/scme/plugins/${pluginId}`);
        if (!cancel) setData(resp.data || null);
      } catch (err) {
        toast.error(formatApiError(err?.response?.data?.detail) || "Error cargando plugin");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [open, pluginId]);

  const plugin = data?.plugin;
  const dep = data?.dependency || {};
  const reuse = data?.reuseByProject || {};
  const projects = data?.projects || [];
  const coupling = plugin ? COUPLING_BAND(plugin.reusabilityScore || 0) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="plugin-detail-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-8">
            <Puzzle className="h-5 w-5 text-orange-600" />
            {plugin?.name || "Plugin"}
            {plugin && <StatusBadge status={plugin.status} />}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Ficha de solo lectura · SCME Fase 2 · No instala ni sincroniza nada.
          </DialogDescription>
        </DialogHeader>

        {loading || !plugin ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
          </div>
        ) : (
          <div className="space-y-4 text-sm">
            {/* Cabecera */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Field label="Plugin ID" mono>{plugin.pluginId}</Field>
              <Field label="Versión" mono>v{plugin.version}</Field>
              <Field label="Categoría">
                <Badge variant="secondary" className="capitalize">{plugin.category}</Badge>
              </Field>
              <Field label="Proyecto origen" mono>{plugin.sourceProject}</Field>
            </div>

            <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-3">
              <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold mb-1">
                Descripción
              </div>
              <p className="text-neutral-800 leading-relaxed">{plugin.description}</p>
            </div>

            {/* Métricas técnicas */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <MetricCard icon={GitBranch} label="Nivel de reutilización">
                <div className="flex items-center gap-2">
                  <span className={`text-lg font-bold font-mono ${REUSE_BAND(plugin.reusabilityScore)}`}>
                    {plugin.reusabilityScore}%
                  </span>
                </div>
              </MetricCard>
              <MetricCard icon={Layers3} label="Nivel de acoplamiento">
                <span className={`inline-block rounded-md px-2 py-1 text-sm font-bold ${coupling.cls}`}>
                  {coupling.label}
                </span>
              </MetricCard>
              <MetricCard icon={Server} label="Stack técnico">
                <div className="flex flex-wrap gap-1 text-[11px]">
                  {plugin.framework && plugin.framework !== "N/A" && (
                    <Badge variant="outline">{plugin.framework}</Badge>
                  )}
                  {plugin.backend && plugin.backend !== "N/A" && (
                    <Badge variant="outline">{plugin.backend}</Badge>
                  )}
                  {plugin.database && plugin.database !== "N/A" && (
                    <Badge variant="outline">{plugin.database}</Badge>
                  )}
                </div>
              </MetricCard>
            </div>

            {/* Dependencias */}
            <Section title="Dependencias" icon={Database}>
              {plugin.dependencies?.length ? (
                <ul className="space-y-1">
                  {plugin.dependencies.map((d, i) => (
                    <li key={i} className="flex items-center justify-between text-[13px] bg-white border border-neutral-200 rounded-md px-2 py-1.5">
                      <div className="flex items-center gap-2">
                        <code className="font-mono text-neutral-800">{d.name}</code>
                        <span className="text-[10px] rounded bg-neutral-100 text-neutral-600 px-1.5 py-0.5 uppercase">{d.type}</span>
                      </div>
                      {d.critical === false ? (
                        <span className="text-[10px] text-neutral-500">opcional</span>
                      ) : (
                        <span className="text-[10px] font-semibold text-orange-700">crítica</span>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-neutral-500 italic">Sin dependencias externas.</p>
              )}
              {dep?.internalPlugins?.length > 0 && (
                <div className="mt-2">
                  <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold mb-1">
                    Plugins internos referenciados
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {dep.internalPlugins.map((p) => (
                      <Badge key={p} variant="outline" className="font-mono text-[10px]">{p}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </Section>

            {/* Variables env */}
            <Section title="Variables de entorno requeridas" icon={KeyRound}>
              {plugin.envVarsRequired?.length ? (
                <div className="flex flex-wrap gap-1.5">
                  {plugin.envVarsRequired.map((v) => (
                    <code key={v} className="text-[11px] font-mono bg-neutral-900 text-neutral-100 rounded px-1.5 py-0.5">
                      {v}
                    </code>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-neutral-500 italic">Ninguna.</p>
              )}
              <p className="text-[10px] text-neutral-500 mt-1">
                Nunca se muestran valores. Sólo nombres de variables declarados en el manifiesto.
              </p>
            </Section>

            {/* Notas técnicas */}
            {plugin.couplingNotes && (
              <Section title="Notas técnicas" icon={Info}>
                <p className="text-[13px] text-neutral-700 leading-relaxed">{plugin.couplingNotes}</p>
              </Section>
            )}

            {/* API pública */}
            {(plugin.publicApi?.endpoints?.length ||
              plugin.publicApi?.hooks?.length ||
              plugin.publicApi?.functions?.length) > 0 && (
              <Section title="API pública" icon={BookOpen}>
                {plugin.publicApi?.endpoints?.length > 0 && (
                  <div className="mb-2">
                    <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold mb-1">Endpoints</div>
                    <ul className="space-y-0.5">
                      {plugin.publicApi.endpoints.map((e) => (
                        <li key={e} className="font-mono text-[11px] text-neutral-800">{e}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {plugin.publicApi?.hooks?.length > 0 && (
                  <div className="mb-2">
                    <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold mb-1">Hooks</div>
                    <ul className="space-y-0.5">
                      {plugin.publicApi.hooks.map((h) => (
                        <li key={h} className="font-mono text-[11px] text-neutral-800">{h}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {plugin.publicApi?.functions?.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold mb-1">Funciones</div>
                    <ul className="space-y-0.5">
                      {plugin.publicApi.functions.map((f) => (
                        <li key={f} className="font-mono text-[11px] text-neutral-800">{f}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </Section>
            )}

            {/* Reutilización cruzada */}
            {projects.length > 0 && (
              <Section title="Estado de reutilización por proyecto" icon={Layers3}>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-[12px]">
                    <thead>
                      <tr className="text-left border-b border-neutral-200 text-neutral-500 uppercase tracking-wider text-[10px]">
                        <th className="py-1.5 pr-4">Proyecto</th>
                        <th className="py-1.5">Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {projects.map((p) => {
                        const st = reuse[p.slug] || "not-applicable";
                        const stStyle = {
                          origin:          "bg-blue-100 text-blue-800",
                          candidate:       "bg-emerald-100 text-emerald-800",
                          pending:         "bg-amber-100 text-amber-800",
                          "not-applicable":"bg-neutral-100 text-neutral-600",
                        }[st] || "bg-neutral-100 text-neutral-600";
                        return (
                          <tr key={p.slug} className="border-b border-neutral-100 last:border-0">
                            <td className="py-1.5 pr-4 font-mono">{p.slug}</td>
                            <td className="py-1.5">
                              <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-medium ${stStyle}`}>
                                {st}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p className="text-[10px] text-neutral-500 mt-2 italic">
                  * Solo información. Ningún proyecto adopta código automáticamente.
                </p>
              </Section>
            )}

            {/* Ficheros fuente */}
            {plugin.files?.length > 0 && (
              <Section title="Ficheros fuente (referencia documental)" icon={BookOpen}>
                <ul className="space-y-0.5">
                  {plugin.files.map((f) => (
                    <li key={f} className="font-mono text-[11px] text-neutral-700">{f}</li>
                  ))}
                </ul>
              </Section>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold mb-0.5">{label}</div>
      <div className={mono ? "font-mono text-[13px] text-neutral-800" : "text-[13px] text-neutral-800"}>
        {children}
      </div>
    </div>
  );
}

function MetricCard({ icon: Icon, label, children }) {
  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-neutral-500 font-bold mb-1.5">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      {children}
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-3">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="h-4 w-4 text-neutral-500" />
        <h4 className="font-semibold text-sm text-neutral-800">{title}</h4>
      </div>
      {children}
    </div>
  );
}

export default function PluginsEcosystemPanel() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("__all__");
  const [status, setStatus] = useState("__all__");
  const [openId, setOpenId] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const [pluginsResp, metaResp] = await Promise.all([
          api.get("/scme/plugins"),
          api.get("/scme/meta"),
        ]);
        if (cancel) return;
        setData(pluginsResp.data || null);
        setMeta(metaResp.data || null);
      } catch (err) {
        toast.error(formatApiError(err?.response?.data?.detail) || "Error cargando el catálogo SCME");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  const plugins = data?.plugins || [];
  const categories = data?.filters?.categories || [];
  const statuses = data?.filters?.statuses || [];

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return plugins.filter((p) => {
      if (category !== "__all__" && p.category !== category) return false;
      if (status !== "__all__" && p.status !== status) return false;
      if (!needle) return true;
      return (
        (p.name || "").toLowerCase().includes(needle) ||
        (p.pluginId || "").toLowerCase().includes(needle) ||
        (p.description || "").toLowerCase().includes(needle) ||
        (p.category || "").toLowerCase().includes(needle)
      );
    });
  }, [plugins, q, category, status]);

  return (
    <div className="card-surface p-4 space-y-4" data-testid="settings-plugins-ecosystem">
      {/* Cabecera */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Puzzle className="h-5 w-5 text-orange-600" />
            <h3 className="font-semibold text-lg">Ecosistema de Plugins</h3>
            <span className="text-[10px] font-mono rounded bg-orange-100 text-orange-800 px-1.5 py-0.5">
              SCME v0.2
            </span>
            <span className="inline-flex items-center gap-1 text-[10px] font-medium rounded bg-neutral-100 text-neutral-700 px-1.5 py-0.5">
              <ShieldCheck className="h-3 w-3" /> Solo lectura
            </span>
          </div>
          <p className="text-xs text-neutral-500 mt-1 max-w-2xl">
            Catálogo corporativo de módulos reutilizables detectados en este proyecto.
            Ningún plugin se instala, exporta ni sincroniza desde aquí — es documentación viva.
            Los proyectos <code className="font-mono">naturautoescuela.net</code>,{" "}
            <code className="font-mono">naturautoescuela.com</code> y{" "}
            <code className="font-mono">radarupfinance.com</code> permanecen 100% independientes.
          </p>
        </div>
        {meta && (
          <div className="text-right text-[11px] text-neutral-500 shrink-0 space-y-2">
            <div>
              <div><span className="font-mono">{meta.totalPlugins}</span> plugins catalogados</div>
              <div className="mt-0.5">Fuente: <span className="font-mono">{meta.scope}</span></div>
              <div className="mt-0.5">Generado: {meta.generatedAt}</div>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setExportOpen(true)}
                data-testid="scme-open-export"
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-md border border-orange-200 bg-orange-50 text-orange-800 hover:bg-orange-100"
              >
                <Download className="h-3.5 w-3.5" /> Exportar ZIP
              </button>
              <button
                type="button"
                onClick={() => setImportOpen(true)}
                data-testid="scme-open-import"
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-md border border-neutral-300 bg-white text-neutral-800 hover:bg-neutral-50"
              >
                <Upload className="h-3.5 w-3.5" /> Importar ZIP
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Filtros */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
        <div className="md:col-span-6 relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400 pointer-events-none" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nombre, ID, categoría o descripción…"
            data-testid="plugins-search"
            className="w-full pl-9 pr-8 py-2 rounded-lg border border-neutral-300 bg-white text-sm focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none"
          />
          {q && (
            <button
              type="button"
              onClick={() => setQ("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700"
              aria-label="Limpiar búsqueda"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          data-testid="plugins-filter-category"
          className="md:col-span-3 py-2 px-3 rounded-lg border border-neutral-300 bg-white text-sm focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none"
        >
          <option value="__all__">Todas las categorías</option>
          {categories.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          data-testid="plugins-filter-status"
          className="md:col-span-3 py-2 px-3 rounded-lg border border-neutral-300 bg-white text-sm focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none"
        >
          <option value="__all__">Todos los estados</option>
          {statuses.map((s) => (
            <option key={s} value={s}>{STATUS_STYLES[s]?.label || s}</option>
          ))}
        </select>
      </div>

      {/* Tabla */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed border-neutral-300 bg-neutral-50 py-8 text-center text-sm text-neutral-500">
          Sin resultados con los filtros actuales.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-neutral-200 bg-white">
          <table className="min-w-full text-sm table-fixed" data-testid="plugins-table">
            <colgroup>
              <col />
              <col style={{ width: "140px" }} />
              <col style={{ width: "90px" }} />
              <col style={{ width: "130px" }} />
              <col style={{ width: "180px" }} />
              <col style={{ width: "180px" }} />
            </colgroup>
            <thead className="bg-neutral-50 text-neutral-600">
              <tr className="text-left text-[10px] uppercase tracking-wider">
                <th className="py-3 px-4 font-bold">Plugin</th>
                <th className="py-3 px-4 font-bold">Categoría</th>
                <th className="py-3 px-4 font-bold">Versión</th>
                <th className="py-3 px-4 font-bold">Estado</th>
                <th className="py-3 px-4 font-bold">Proyecto origen</th>
                <th className="py-3 px-4 font-bold">Reutilización</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr
                  key={p.pluginId}
                  onClick={() => setOpenId(p.pluginId)}
                  data-testid={`plugin-row-${p.pluginId}`}
                  className="border-t border-neutral-100 hover:bg-orange-50/40 cursor-pointer transition-colors h-[56px]"
                >
                  <td className="py-3 px-4 align-middle">
                    <div className="font-semibold text-neutral-900 truncate" title={p.name}>{p.name}</div>
                    <div className="font-mono text-[10px] text-neutral-500 truncate">{p.pluginId}</div>
                  </td>
                  <td className="py-3 px-4 align-middle">
                    <Badge variant="secondary" className="capitalize text-[10px]">{p.category}</Badge>
                  </td>
                  <td className="py-3 px-4 font-mono text-[12px] text-neutral-700 align-middle">v{p.version}</td>
                  <td className="py-3 px-4 align-middle"><StatusBadge status={p.status} /></td>
                  <td className="py-3 px-4 font-mono text-[11px] text-neutral-600 align-middle truncate">{p.sourceProject}</td>
                  <td className="py-3 px-4 align-middle"><ReusePill score={p.reusabilityScore} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="text-[10px] text-neutral-500 italic flex items-start gap-1.5">
        <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
        <span>
          SCME es un sistema de conocimiento. Si un proyecto desaparece, los demás siguen funcionando.
          Cualquier reutilización futura implicará una copia validada explícita, nunca una dependencia en tiempo real.
        </span>
      </div>

      <PluginDetailDialog
        pluginId={openId}
        open={!!openId}
        onOpenChange={(v) => { if (!v) setOpenId(null); }}
      />

      <PluginsExportDialog open={exportOpen} onOpenChange={setExportOpen} />
      <PluginsImportDialog open={importOpen} onOpenChange={setImportOpen} />
    </div>
  );
}
